import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type UserlikeChatbotModuleSpecsFragment } from '../../../../api/fragments/UserlikeChatbotModuleSpecs';
import { type UserlikeChatbotSettingsSpecFragment } from '../../../../api/fragments/UserlikeChatbotSettingsSpec';
import {
    UpdateUserlikeChatbotSettingsDocument,
    type UpdateUserlikeChatbotSettingsMutation,
    type UpdateUserlikeChatbotSettingsMutationVariables,
} from '../../../../api/mutations/updateUserlikeChatbotSettings';
import { type UserlikeChatbotSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = UserlikeChatbotSettingsInput;

export type UpdateAdyenPaymentSettingInnerPageProps = {
    module: UserlikeChatbotModuleSpecsFragment;
    settings: UserlikeChatbotSettingsSpecFragment;
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('script')
);

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdateUserlikeChatbotSettingInnerPage = ({ module, settings }: UpdateAdyenPaymentSettingInnerPageProps) => {
    const moduleId = module.id;
    const settingsId = settings.id;

    const { t } = useTranslation(['userlikeChatbotModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const initialValues = useMemo((): FormValues => omit(['id', 'liveChatModuleId'], settings), [settings]);

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('userlikeChatbotModuleDetails:settingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateUserlikeChatbotSettingsMutation, UpdateUserlikeChatbotSettingsMutationVariables>({
                    mutation: UpdateUserlikeChatbotSettingsDocument,
                    variables: { moduleId, id: settingsId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('userlikeChatbotModuleDetails:settingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, settingsId, navigate]
    );

    const validate = useValidator(validator);

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="userlikeChatbotSettingsForm" htmlType="submit" type="primary">
                    {t('userlikeChatbotModuleDetails:settingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('userlikeChatbotModuleDetails:settingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="userlikeChatbotSettingsForm"
                        layout="vertical"
                        name="userlikeChatbotSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Card title={t('userlikeChatbotModuleDetails:dividers.mainDetails')}>
                            <Row gutter={14}>
                                <Col {...colSpan}>
                                    <InputField
                                        {...t('userlikeChatbotModuleDetails:settingsDrawer.fields.displayName', {
                                            returnObjects: true,
                                        })}
                                        name="displayName"
                                        required
                                    />
                                </Col>
                                <Col {...colSpan}>
                                    <InputField
                                        {...t('userlikeChatbotModuleDetails:settingsDrawer.fields.script', {
                                            returnObjects: true,
                                        })}
                                        name="script"
                                    />
                                </Col>
                            </Row>
                        </Card>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateUserlikeChatbotSettingInnerPage;
